import { SIMULATABLE_ROLES, isSimulatableRole } from "./checkingRole";

/**
 * "Look at this dial as…" — the KEY-498 role toggle, shown inside the checking banner.
 *
 * Checking makes every checker an Admin on the test dial, and Admin passes every role gate in the product.
 * So the one place per-status `allowedRoles`, the action matrix and the per-attribute rules are meant to be
 * tried out is the one place they were all bypassed. This asks the SERVER to evaluate the caller as one
 * other role instead — not a client-side pretence, because the things most worth checking (a restricted
 * field's value, a restricted message, the files behind them) are pruned from the payload before the client
 * ever sees them, and a presentation-only toggle would show a "Reader" what a real Reader never receives.
 *
 * Rendered from `simulatedRole` — what the server reported it actually evaluated as — rather than from local
 * state, so a request the server declines shows up as the control snapping back to "My own role".
 */
export function CheckingRoleToggle({
  simulatedRole,
  onChange,
  busy,
}: {
  /** The role the SERVER says it evaluated as, or null for "my own role". */
  simulatedRole: string | null;
  /** Ask for a different role (or null to stop simulating). The dial reloads under the new role. */
  onChange: (role: string | null) => void;
  /** True while the reload is in flight, so the buttons don't invite a second click mid-swap. */
  busy?: boolean;
}) {
  const active = isSimulatableRole(simulatedRole) ? simulatedRole : null;

  return (
    <div style={{ marginTop: 12 }}>
      <div className="kd-label-overline" style={{ marginBottom: 6 }}>
        Look at this dial as
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <RoleButton label="My own role" selected={active === null} busy={busy} onClick={() => onChange(null)} />
        {SIMULATABLE_ROLES.map((role) => (
          <RoleButton
            key={role}
            label={role}
            selected={active === role}
            busy={busy}
            onClick={() => onChange(role)}
          />
        ))}
      </div>
      <p style={{ margin: "8px 0 0", color: "var(--text-muted)", fontSize: 13, lineHeight: 1.55 }}>
        You'll still see the whole dial — only what this role is <em>allowed to do</em> changes. A role is
        exactly itself, with nothing inherited: if this dial only lets Reviewers set a status, an Editor gets
        a dial they can read but not change. That isn't the dial being broken; it's the answer you came for.
      </p>
    </div>
  );
}

function RoleButton({
  label,
  selected,
  busy,
  onClick,
}: {
  label: string;
  selected: boolean;
  busy?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="kd-btn kd-btn-sm"
      onClick={onClick}
      disabled={busy || selected}
      aria-pressed={selected}
      style={
        selected
          ? {
              // Selected reads as chosen rather than disabled, even though it is disabled (clicking the role
              // you are already in has nothing to do).
              background: "var(--accent-soft)",
              borderColor: "var(--accent-border)",
              color: "var(--accent-text)",
              opacity: 1,
            }
          : undefined
      }
    >
      {label}
    </button>
  );
}
